import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { jobsApi, commentsApi, messagesApi, reviewsApi, authApi, calendarApi, ApiError } from "./src/utils/api";
import { checkIcelandicText, debounce } from "./src/utils/proofreader";
import { theme, baseStyle } from "./src/theme";
import { formatTimeAgo } from "./src/utils/formatters";
import { toCamelCase } from "./src/utils/fieldMapper";
import { setSentryUser, clearSentryUser } from "./src/utils/sentry";
import { PATH_TO_SCREEN, SCREEN_TO_PATH, screenFromPath } from "./src/utils/router";
import { useWebSocket } from "./src/hooks/useWebSocket";
import useOffers from "./src/hooks/useOffers";
import useJobActions from "./src/hooks/useJobActions";
import useRagga from "./src/hooks/useRagga";
import useFontSize from "./src/hooks/useFontSize";
// Screen components — static (needed immediately)
import AuthScreen from "./src/components/screens/AuthScreen";
import FeedScreen from "./src/components/screens/FeedScreen";
// Screen components — lazy-loaded (on-demand)
const SearchScreen = lazy(() => import("./src/components/screens/SearchScreen"));
const JobScreen = lazy(() => import("./src/components/screens/JobScreen"));
const NewJobScreen = lazy(() => import("./src/components/screens/NewJobScreen"));
const ChatScreen = lazy(() => import("./src/components/screens/ChatScreen"));
const ProfileScreen = lazy(() => import("./src/components/screens/ProfileScreen"));
const MyPageScreen = lazy(() => import("./src/components/screens/MyPageScreen"));
const RaggiScreen = lazy(() => import("./src/components/screens/RaggiScreen"));
const CalendarScreen = lazy(() => import("./src/components/screens/CalendarScreen"));
const OnboardingScreen = lazy(() => import("./src/components/screens/OnboardingScreen"));
const TermsScreen = lazy(() => import("./src/components/screens/TermsScreen"));
const DriverScreen = lazy(() => import("./src/components/screens/DriverScreen"));
const AdminScreen = lazy(() => import("./src/components/screens/AdminScreen"));
const LandingScreen = lazy(() => import("./src/components/screens/LandingScreen"));
import OfflineIndicator from "./src/components/ui/OfflineIndicator";
import PwaInstallBanner from "./src/components/ui/PwaInstallBanner";
import ErrorBoundary from "./src/components/ui/ErrorBoundary";

// ============================================================
// MAIN APP — Thin Coordinator
// ============================================================

export default function GreidiApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated, loginWithRedirect, loginWithPopup, logout, user, getAccessTokenSilently } = useAuth0();
  const { fontScale, fontLabel, cycleFontSize, highContrast, toggleContrast } = useFontSize();
  const [screen, setScreen] = useState(() => screenFromPath(window.location.pathname).screen);
  const [hasOnboarded, setHasOnboarded] = useState(() => localStorage.getItem('greidi_onboarded') === 'true');

  // DEV MODE
  const isDev = import.meta.env.DEV && !import.meta.env.VITE_AUTH0_DOMAIN;
  const [devUser, setDevUser] = useState(null);
  const [dbUser, setDbUser] = useState(null); // D1 user data from sync

  const isGuest = !isAuthenticated && !isDev;
  const currentUser = user ? {
    id: dbUser?.id || user.sub,
    name: user.name || user.nickname || "Notandi",
    phone: dbUser?.phone || "",
    rating: dbUser?.rating || 5.0,
    jobs: dbUser?.total_jobs || 0,
    avatar: user.picture ? "👤" : "🏠",
    bio: dbUser?.bio || "Greiði notandi",
    role: dbUser?.role || "user",
  } : (isDev
    ? (devUser || { id: "dev", name: "Próf notandi", phone: "899-0000", rating: 5.0, jobs: 0, avatar: "🧪", bio: "Dev mode" })
    : { id: "guest", name: "Gestur", phone: "", rating: 0, jobs: 0, avatar: "👤", bio: "" });

  // View state
  const [selectedJob, setSelectedJob] = useState(null);
  const [chatJob, setChatJob] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [selectedArea, setSelectedArea] = useState(() => localStorage.getItem('greidi_default_area') || "Allt");
  const [filterCat, setFilterCat] = useState(null);
  const [newJobStep, setNewJobStep] = useState(0);
  const [newJobData, setNewJobData] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [notification, setNotification] = useState(null);

  // Data state
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState(null);
  const [myJobs, setMyJobs] = useState([]);
  const [claimedJobs, setClaimedJobs] = useState([]);
  const [chatMessages, setChatMessages] = useState({});
  const [chatLoading, setChatLoading] = useState(false);
  const [chatToken, setChatToken] = useState(null);
  const [msgInput, setMsgInput] = useState("");
  const [activeTab, setActiveTab] = useState("feed");
  const [searchText, setSearchText] = useState("");
  const [calendarJobs, setCalendarJobs] = useState({});

  // Comments state
  const [jobComments, setJobComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentInput, setCommentInput] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Timeline events state
  const [jobEvents, setJobEvents] = useState([]);

  // Reviews state
  const [profileReviews, setProfileReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Unread & Ragga
  const [unreadCount, setUnreadCount] = useState(0);
  const [raggaMessages, setRaggaMessages] = useState([
    { from: "ragga", text: "Hæ! Ég er Ragga, snjöll ráðgjafi Greiða. Hvernig get ég aðstoðað þig í dag? 😊", time: "núna" }
  ]);
  const [raggaInput, setRaggaInput] = useState("");

  // ── Early definitions (needed by hooks) ────────────────────
  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(null), 3000); };
  // Navigate helper: updates both screen state AND browser URL
  const navTo = useCallback((screenName, path) => {
    setScreen(screenName);
    if (path && location.pathname !== path) navigate(path, { replace: false });
  }, [navigate, location.pathname]);
  const goFeed = () => { navTo("feed", "/"); setActiveTab("feed"); setFilterCat(null); };

  // ── Custom Hooks ──────────────────────────────────────────
  const { jobOffers, offersLoading, showOfferForm, setShowOfferForm, offerAmount, setOfferAmount, offerMessage, setOfferMessage, offerSubmitting, offerTransport, setOfferTransport, offerPickupAddress, setOfferPickupAddress, offerPickupLat, setOfferPickupLat, offerPickupLng, setOfferPickupLng, acceptedOffer, fetchOffers, fetchAcceptedOffer, handleSubmitOffer, handleAcceptOffer, handleRejectOffer, resetOfferForm } = useOffers({ isAuthenticated, isDev, notify, loginWithRedirect, getAccessTokenSilently });
  const { handleDeleteJob, handleReleaseJob, handleCreateAddon, handleStartJob, handleCompleteJob, handleSetPayment, handleUploadPhoto, handleEditJob } = useJobActions({ isAuthenticated, notify, loginWithRedirect, getAccessTokenSilently, jobs, setJobs, setSelectedJob, goFeed });
  const { triggerRaggaComment, handleRequestRaggaComment } = useRagga({ isAuthenticated, isGuest, isDev, notify, loginWithRedirect, getAccessTokenSilently, jobComments, setJobComments });

  // Proofreading
  const [proofResults, setProofResults] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [descriptionText, setDescriptionText] = useState("");
  const [titleProofResults, setTitleProofResults] = useState(null);
  const [titleProofLoading, setTitleProofLoading] = useState(false);

  const debouncedProofcheck = useCallback(
    debounce(async (text) => {
      if (!text || text.trim().length < 5) { setProofResults(null); setProofLoading(false); return; }
      setProofLoading(true);
      try { const result = await checkIcelandicText(text); setProofResults(result); }
      catch (e) { console.warn("Stafsetningarprófun mistókst:", e); setProofResults(null); }
      finally { setProofLoading(false); }
    }, 800),
    []
  );

  const debouncedTitleProofcheck = useCallback(
    debounce(async (text) => {
      if (!text || text.trim().length < 3) { setTitleProofResults(null); setTitleProofLoading(false); return; }
      setTitleProofLoading(true);
      try { const result = await checkIcelandicText(text); setTitleProofResults(result); }
      catch (e) { console.warn("Stafsetningarprófun titils mistókst:", e); setTitleProofResults(null); }
      finally { setTitleProofLoading(false); }
    }, 800),
    []
  );

  // ── Effects ──────────────────────────────────────────────
  // Focus management: scroll to top and focus main content on screen change
  const mainRef = useRef(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      if (mainRef.current) mainRef.current.focus({ preventScroll: true });
    }, 50);
    return () => clearTimeout(timer);
  }, [screen]);

  // Accessibility: apply font scale and contrast on root element
  useEffect(() => {
    document.documentElement.style.zoom = fontScale;
    document.documentElement.style.filter = highContrast ? "contrast(1.25) brightness(1.1)" : "";
    return () => { document.documentElement.style.zoom = ''; document.documentElement.style.filter = ''; };
  }, [fontScale, highContrast]);

  // Clear Sentry user context on logout
  useEffect(() => {
    if (!isAuthenticated && !isDev) clearSentryUser();
  }, [isAuthenticated, isDev]);

  // Force re-auth if cached tokens lack email claim (one-time after scope fix)
  useEffect(() => {
    if (isAuthenticated && user && !isLoading && !user.email) {
      console.warn('[Greiði] Cached tokens lack email — forcing re-login');
      loginWithRedirect();
    }
  }, [isAuthenticated, user, isLoading, loginWithRedirect]);

  // Sync Auth0 user to database and store D1 data
  useEffect(() => {
    const syncUser = async () => {
      if (isAuthenticated && user && !isLoading && user.email) {
        try {
          const token = await getAccessTokenSilently();
          const res = await authApi.syncUser({
            email: user.email, name: user.name || user.nickname || 'Notandi',
            picture: user.picture || '', phone: user.phone_number || '',
          }, () => token);
          if (res?.data) setDbUser(res.data);
          setSentryUser(user);
        } catch (error) { console.error('Failed to sync user:', error); }
      }
    };
    syncUser();
  }, [isAuthenticated, user, isLoading, getAccessTokenSilently]);

  // Map raw API job (snake_case) to frontend format (camelCase)
  const mapJob = (job) => {
    const j = toCamelCase(job);
    return {
      ...j,
      // Computed/alias fields
      desc: j.description,
      img: j.imageUrl || "🖼️",
      iconUrl: j.iconUrl || null,
      tier: j.priceTier || "samkomulag",
      aiDesc: j.description,
      timeAgo: formatTimeAgo(j.createdAt),
      price: j.price || "Samkomulag",
      // Defaults
      jobType: j.jobType || "standard",
      claimedBy: j.claimedBy || null,
      weightClass: j.weightClass || 'light',
      weightKg: j.weightKg || 0,
      needsExtraPerson: j.needsExtraPerson || 0,
      floorLevel: j.floorLevel || 0,
      hasElevator: j.hasElevator || 0,
      disclaimerAccepted: j.disclaimerAccepted || 0,
      squareMeters: j.squareMeters || null,
      distanceKm: j.distanceKm || null,
      fuelConsumption: j.fuelConsumption || null,
      drivingCost: j.drivingCost || null,
      comments: [], offers: 0,
    };
  };

  // Fetch open jobs for feed
  const fetchJobs = useCallback(async () => {
    try {
      setJobsLoading(true); setJobsError(null);
      const response = await jobsApi.list({ status: 'open', limit: 50 });
      setJobs(response.data.map(mapJob));
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      setJobsError(error instanceof ApiError ? error.message : 'Ekki tókst að sækja verk');
      setJobs([]);
    } finally { setJobsLoading(false); }
  }, []);

  // Fetch user's own jobs + claimed jobs for MyPage
  const fetchMyJobs = useCallback(async () => {
    if (!currentUser?.id || currentUser.id === "guest") return;
    try {
      const [ownRes, claimedRes] = await Promise.all([
        jobsApi.list({ user_id: currentUser.id, limit: 50 }),
        jobsApi.list({ claimed_by: currentUser.id, limit: 50 }),
      ]);
      setMyJobs(ownRes.data.map(mapJob));
      setClaimedJobs(claimedRes.data.map(mapJob));
    } catch (error) {
      console.error('Failed to fetch my jobs:', error);
    }
  }, [currentUser?.id]);

  // Fetch jobs on mount
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Fetch unread count periodically
  useEffect(() => {
    const fetchUnread = async () => {
      if (!isAuthenticated || isDev) return;
      try { const result = await messagesApi.getUnreadCount(getAccessTokenSilently); setUnreadCount(result.count || 0); }
      catch (err) { console.error('Failed to fetch unread count:', err); }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isDev, getAccessTokenSilently]);

  // ── WebSocket Chat ─────────────────────────────────────
  // Get access token when entering chat screen
  useEffect(() => {
    if (screen !== "chat" || !chatJob || (!isAuthenticated && !isDev)) { setChatToken(null); return; }
    let mounted = true;
    (async () => {
      try { const t = await getAccessTokenSilently(); if (mounted) setChatToken(t); }
      catch (e) { console.warn('WS token failed:', e); }
    })();
    return () => { mounted = false; };
  }, [screen, chatJob, isAuthenticated, isDev, getAccessTokenSilently]);

  const wsJobId = screen === "chat" && chatJob ? chatJob.id : null;
  const wsUserId = currentUser?.id !== "guest" ? currentUser?.id : null;
  const { connected: wsConnected, typingUsers: wsTypingUsers, onlineCount: wsOnlineCount, sendMessage: wsSendMessage, sendTyping: wsSendTyping, markRead: wsMarkRead } = useWebSocket(
    wsJobId, chatToken, wsUserId,
    {
      onNewMessage: (msg) => {
        const mm = toCamelCase(msg);
        if (mm.fromUserId === currentUser?.id) return; // Already shown via optimistic update
        setChatMessages(prev => {
          const jobMsgs = prev[mm.jobId] || [];
          if (jobMsgs.some(m => m.id === mm.id)) return prev;
          return { ...prev, [mm.jobId]: [...jobMsgs, { ...mm, from: mm.fromUserId, fromName: mm.fromName || 'Notandi', time: 'núna' }] };
        });
      },
      onReadReceipt: (messageId) => {
        setChatMessages(prev => {
          const updated = { ...prev };
          for (const jobId of Object.keys(updated)) {
            const msgs = updated[jobId];
            const idx = msgs.findIndex(m => m.id === messageId);
            if (idx !== -1) {
              updated[jobId] = msgs.map(m => m.id === messageId ? { ...m, read: true } : m);
              break;
            }
          }
          return updated;
        });
      },
    }
  );

  // ── Fetch helpers ────────────────────────────────────────
  const fetchComments = async (jobId) => {
    setCommentsLoading(true);
    try {
      const result = await commentsApi.list(jobId);
      setJobComments((result.data || []).map(c => {
        const cc = toCamelCase(c);
        return { ...cc, userName: cc.userName || 'Notandi', userAvatar: cc.userAvatar || '👤', time: formatTimeAgo(cc.createdAt) };
      }));
    } catch (err) { console.error('Failed to fetch comments:', err); setJobComments([]); }
    finally { setCommentsLoading(false); }
  };

  const fetchJobEvents = async (jobId) => {
    if (!isAuthenticated && !isDev) return;
    try {
      const result = await jobsApi.getEvents(jobId, getAccessTokenSilently);
      setJobEvents((result.data || []).map(toCamelCase));
    } catch (err) { console.error('Failed to fetch events:', err); setJobEvents([]); }
  };

  const fetchMessages = async (jobId) => {
    if (!isAuthenticated && !isDev) return;
    setChatLoading(true);
    try {
      const result = await messagesApi.list(jobId, getAccessTokenSilently);
      const messages = (result.data || []).map(m => {
        const mm = toCamelCase(m);
        return { ...mm, from: mm.fromUserId === (user?.sub || currentUser.id) ? "me" : mm.fromUserId,
          fromName: mm.fromName || 'Notandi', time: formatTimeAgo(mm.createdAt) };
      });
      setChatMessages(prev => ({ ...prev, [jobId]: messages }));
      for (const m of messages) {
        if (!m.read && m.from !== "me") messagesApi.markRead(m.id, getAccessTokenSilently).catch(() => {});
      }
    } catch (err) { console.error('Failed to fetch messages:', err); }
    finally { setChatLoading(false); }
  };

  const fetchReviews = async (userId) => {
    setReviewsLoading(true);
    try {
      const result = await reviewsApi.list(userId);
      // API returns { data: { reviews: [...], avg_rating, total_reviews } }
      const reviews = Array.isArray(result.data) ? result.data : (result.data?.reviews || []);
      setProfileReviews(reviews.map(r => {
        const rr = toCamelCase(r);
        return { ...rr, from: rr.fromUserName || rr.fromName || 'Notandi', stars: rr.rating,
          text: rr.comment || rr.text || "", time: formatTimeAgo(rr.createdAt) };
      }));
    } catch (err) { console.error('Failed to fetch reviews:', err); setProfileReviews([]); }
    finally { setReviewsLoading(false); }
  };

  // ── Calendar ────────────────────────────────────────────
  const loadCalendarJobs = useCallback(async (startDate, endDate, area) => {
    try {
      const res = await calendarApi.getJobsByDateRange(startDate, endDate, area !== "Allt" ? area : undefined);
      if (res.success && res.data?.jobs_by_date) {
        // Map each job through toCamelCase
        const mapped = {};
        Object.entries(res.data.jobs_by_date).forEach(([dateKey, dateJobs]) => {
          mapped[dateKey] = dateJobs.map(mapJob);
        });
        setCalendarJobs(mapped);
      }
    } catch (err) {
      console.warn('Failed to load calendar jobs:', err);
    }
  }, []);

  // ── Navigation ───────────────────────────────────────────
  const goJob = (job) => {
    setSelectedJob(job); navTo("job", `/verk/${job.id}`); setCommentInput("");
    resetOfferForm();
    fetchComments(job.id); fetchOffers(job.id); fetchJobEvents(job.id);
    triggerRaggaComment(job);
  };
  const goChat = (job) => {
    if (isGuest) { notify("⚠️ Skráðu þig inn til að spjalla"); loginWithRedirect(); return; }
    setChatJob(job); navTo("chat", `/spjall/${job.id}`); setMsgInput(""); fetchMessages(job.id); fetchAcceptedOffer(job.id);
  };
  const goProfile = (userId) => {
    setProfileUser({ id: userId, name: "Notandi", avatar: "👤", rating: 5.0, bio: "" }); navTo("profile", `/notandi/${userId}`); setShowReviewForm(false);
    setReviewRating(5); setReviewText(""); fetchReviews(userId);
  };
  const goNewJob = () => {
    if (isGuest) { notify("⚠️ Skráðu þig inn til að búa til verk"); loginWithRedirect(); return; }
    setNewJobStep(0); setNewJobData({}); setImagePreview(null); setImageUploading(false);
    setDescriptionText("");
    setProofResults(null); setProofLoading(false); setTitleProofResults(null); setTitleProofLoading(false); navTo("newjob", "/nyttvert");
  };
  const goMyPage = () => {
    if (isGuest) { notify("⚠️ Skráðu þig inn til að sjá síðuna þína"); loginWithRedirect(); return; }
    fetchMyJobs();
    navTo("mypage", "/minsida"); setActiveTab("mypage");
  };
  const goSearch = () => { navTo("search", "/leit"); setActiveTab("search"); };
  const goCalendar = () => { navTo("calendar", "/dagatal"); setActiveTab("calendar"); };
  const goTerms = () => { navTo("terms", "/skilmalar"); };
  const goDriver = () => {
    if (isGuest) { notify("⚠️ Skráðu þig inn til að sjá bílstjórasíðu"); loginWithRedirect(); return; }
    navTo("driver", "/bilstjori");
  };
  const goAdmin = () => {
    if (isGuest) { notify("⚠️ Skráðu þig inn"); loginWithRedirect(); return; }
    if (currentUser?.role !== 'admin') { notify("⚠️ Aðeins stjórnendur hafa aðgang"); return; }
    navTo("admin", "/stjornbord");
  };

  const handleOnboardingComplete = (area) => {
    localStorage.setItem('greidi_onboarded', 'true');
    if (area) {
      localStorage.setItem('greidi_default_area', area);
      setSelectedArea(area);
    }
    setHasOnboarded(true);
    navTo("feed", "/");
  };

  // Sync browser back/forward with screen state
  useEffect(() => {
    const { screen: urlScreen, id } = screenFromPath(location.pathname);
    if (urlScreen !== screen) {
      setScreen(urlScreen);
      // Deep link: load job/chat/profile from URL if not already loaded
      if (urlScreen === 'job' && id && (!selectedJob || selectedJob.id !== id)) {
        // Fetch job by ID for deep linking
        jobsApi.get(id).then(res => {
          if (res.data) {
            const job = mapJob(res.data);
            setSelectedJob(job);
            fetchComments(job.id); fetchOffers(job.id); fetchJobEvents(job.id);
          }
        }).catch(() => navTo("feed", "/"));
      }
    }
  }, [location.pathname]);

  // ── Action Handlers ──────────────────────────────────────
  const handleSubmitComment = async (jobId, text) => {
    if (!text) return;
    if (!isAuthenticated && !isDev) { notify("⚠️ Þú þarft að skrá þig inn"); loginWithRedirect(); return; }
    setCommentSubmitting(true);
    try {
      await commentsApi.create({ job_id: jobId, text }, getAccessTokenSilently);
      setCommentInput(""); notify("💬 Athugasemd bætt við!"); fetchComments(jobId);
    } catch (error) {
      console.error('Failed to create comment:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að senda athugasemd");
    } finally { setCommentSubmitting(false); }
  };

  const handleSendMessage = async (job, text) => {
    if (!text?.trim()) return;
    if (!isAuthenticated && !isDev) { notify("⚠️ Þú þarft að skrá þig inn"); loginWithRedirect(); return; }
    const jobId = job.id;
    const otherUserId = job.userId === currentUser.id ? (job.claimedBy || "u1") : job.userId;
    // Optimistic update
    setChatMessages(prev => ({
      ...prev,
      [jobId]: [...(prev[jobId] || []), { id: 'temp-' + Date.now(), from: "me", text, time: "núna", read: false }],
    }));
    setMsgInput("");
    // Try WebSocket first — ChatRoom DO handles D1 persistence + broadcast
    if (wsSendMessage(text, otherUserId)) return;
    // REST fallback when WS is not connected
    try { await messagesApi.create({ job_id: jobId, to_user_id: otherUserId, text }, getAccessTokenSilently); }
    catch (error) {
      console.error('Failed to send message:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að senda skilaboð");
    }
  };

  const handleSubmitReview = async (userId) => {
    if (!reviewText.trim()) return;
    if (!isAuthenticated && !isDev) { notify("⚠️ Þú þarft að skrá þig inn"); loginWithRedirect(); return; }
    setReviewSubmitting(true);
    try {
      await reviewsApi.create({ to_user_id: userId, rating: reviewRating, comment: reviewText.trim() }, getAccessTokenSilently);
      setShowReviewForm(false); setReviewText(""); setReviewRating(5);
      notify("⭐ Umsögn send!"); fetchReviews(userId);
    } catch (error) {
      console.error('Failed to create review:', error);
      notify(error instanceof ApiError ? `❌ ${error.message}` : "❌ Ekki tókst að senda umsögn");
    } finally { setReviewSubmitting(false); }
  };

  // ── Shared props ─────────────────────────────────────────
  const navProps = { activeTab, unreadCount, goFeed, goSearch, goNewJob, goCalendar, goMyPage, goDriver, goAdmin, notification };

  // ── Screen Router ────────────────────────────────────────
  const lazyFallback = <div style={{ padding: 40, textAlign: "center", color: theme.textMuted }}>Hleð...</div>;

  // Auth0 loading screen (only while SDK initializes)
  if (!isDev && isLoading) {
    return <><OfflineIndicator /><AuthScreen isDev={isDev} isLoading={true} isAuthenticated={false} loginWithRedirect={loginWithRedirect} devUser={devUser} setDevUser={setDevUser} notify={notify} /></>;
  }
  // Dev mode requires local user selection
  if (isDev && !devUser) {
    return <><OfflineIndicator /><AuthScreen isDev={isDev} isLoading={false} isAuthenticated={false} loginWithRedirect={loginWithRedirect} devUser={devUser} setDevUser={setDevUser} notify={notify} /></>;
  }
  // Landing page for unauthenticated users (non-dev)
  if (!isDev && !isAuthenticated) {
    return <><OfflineIndicator /><Suspense fallback={lazyFallback}><LandingScreen onLogin={loginWithRedirect} /></Suspense></>;
  }

  // Onboarding only for authenticated users
  if (!isGuest && !hasOnboarded) {
    return <><OfflineIndicator /><ErrorBoundary key="onboarding"><Suspense fallback={lazyFallback}><OnboardingScreen onComplete={handleOnboardingComplete} /></Suspense></ErrorBoundary></>;
  }

  if (screen === "feed") {
    return <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><FeedScreen jobs={jobs} jobsLoading={jobsLoading} jobsError={jobsError} filterCat={filterCat} setFilterCat={setFilterCat} selectedArea={selectedArea} setSelectedArea={setSelectedArea} goJob={goJob} notify={notify} onRefresh={fetchJobs} {...navProps} /></ErrorBoundary></div></>;
  }

  if (screen === "search") {
    return <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><SearchScreen jobs={jobs} searchText={searchText} setSearchText={setSearchText} selectedArea={selectedArea} setSelectedArea={setSelectedArea} goJob={goJob} {...navProps} /></Suspense></ErrorBoundary></div></>;
  }

  if (screen === "job" && selectedJob) {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><JobScreen
        job={selectedJob} currentUser={currentUser} isGuest={isGuest} loginWithRedirect={loginWithRedirect}
        jobComments={jobComments} setJobComments={setJobComments} commentsLoading={commentsLoading} commentInput={commentInput} setCommentInput={setCommentInput} commentSubmitting={commentSubmitting} handleSubmitComment={handleSubmitComment}
        jobOffers={jobOffers} offersLoading={offersLoading} showOfferForm={showOfferForm} setShowOfferForm={setShowOfferForm} offerAmount={offerAmount} setOfferAmount={setOfferAmount} offerMessage={offerMessage} setOfferMessage={setOfferMessage} offerSubmitting={offerSubmitting} handleSubmitOffer={handleSubmitOffer} handleAcceptOffer={handleAcceptOffer} handleRejectOffer={handleRejectOffer}
        offerTransport={offerTransport} setOfferTransport={setOfferTransport} offerPickupAddress={offerPickupAddress} setOfferPickupAddress={setOfferPickupAddress} offerPickupLat={offerPickupLat} setOfferPickupLat={setOfferPickupLat} offerPickupLng={offerPickupLng} setOfferPickupLng={setOfferPickupLng}
        jobEvents={jobEvents}
        handleDeleteJob={handleDeleteJob} handleEditJob={handleEditJob} handleReleaseJob={handleReleaseJob} handleCreateAddon={handleCreateAddon}
        handleStartJob={handleStartJob} handleCompleteJob={handleCompleteJob} handleSetPayment={handleSetPayment} handleUploadPhoto={handleUploadPhoto}
        handleRequestRaggaComment={handleRequestRaggaComment}
        goFeed={goFeed} goChat={goChat} goProfile={goProfile} goJob={goJob} goTerms={goTerms}
        notify={notify} getAccessTokenSilently={getAccessTokenSilently} isAuthenticated={isAuthenticated} {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "newjob") {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><NewJobScreen
        newJobStep={newJobStep} setNewJobStep={setNewJobStep} newJobData={newJobData} setNewJobData={setNewJobData}
        imagePreview={imagePreview} setImagePreview={setImagePreview} imageUploading={imageUploading} setImageUploading={setImageUploading} fileInputRef={fileInputRef}
        descriptionText={descriptionText} setDescriptionText={setDescriptionText} proofResults={proofResults} setProofResults={setProofResults} proofLoading={proofLoading} setProofLoading={setProofLoading}
        jobs={jobs} setJobs={setJobs} currentUser={currentUser} goFeed={goFeed} goTerms={goTerms} notify={notify}
        getAccessTokenSilently={getAccessTokenSilently} isAuthenticated={isAuthenticated} loginWithRedirect={loginWithRedirect}
        selectedArea={selectedArea} debouncedProofcheck={debouncedProofcheck}
        titleProofResults={titleProofResults} setTitleProofResults={setTitleProofResults} titleProofLoading={titleProofLoading} debouncedTitleProofcheck={debouncedTitleProofcheck}
        {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "chat") {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><ChatScreen
        chatJob={chatJob} selectedJob={selectedJob} chatMessages={chatMessages} chatLoading={chatLoading}
        msgInput={msgInput} setMsgInput={setMsgInput} currentUser={currentUser} handleSendMessage={handleSendMessage}
        acceptedOffer={acceptedOffer}
        wsConnected={wsConnected} wsTypingUsers={wsTypingUsers} wsSendTyping={wsSendTyping} wsOnlineCount={wsOnlineCount} wsMarkRead={wsMarkRead}
        goFeed={goFeed} goJob={goJob} notify={notify} {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "profile" && profileUser) {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><ProfileScreen
        profileUser={profileUser} selectedJob={selectedJob} currentUser={currentUser}
        profileReviews={profileReviews} reviewsLoading={reviewsLoading} showReviewForm={showReviewForm} setShowReviewForm={setShowReviewForm}
        reviewRating={reviewRating} setReviewRating={setReviewRating} reviewText={reviewText} setReviewText={setReviewText}
        reviewSubmitting={reviewSubmitting} handleSubmitReview={handleSubmitReview}
        goFeed={goFeed} goChat={goChat} notify={notify}
        isAuthenticated={isAuthenticated} isDev={isDev} loginWithRedirect={loginWithRedirect} getAccessTokenSilently={getAccessTokenSilently} fetchReviews={fetchReviews}
        {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "calendar") {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><CalendarScreen
        selectedArea={selectedArea !== "Allt" ? selectedArea : null}
        goFeed={goFeed} goJobDetail={goJob}
        currentUser={currentUser}
        getAccessTokenSilently={getAccessTokenSilently}
        isAuthenticated={isAuthenticated}
        calendarJobs={calendarJobs}
        loadCalendarJobs={loadCalendarJobs}
        navProps={navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "ragga") {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><RaggiScreen
        raggaMessages={raggaMessages} setRaggaMessages={setRaggaMessages}
        raggaInput={raggaInput} setRaggaInput={setRaggaInput}
        {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "mypage") {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><MyPageScreen
        currentUser={currentUser} user={user} myJobs={myJobs} claimedJobs={claimedJobs} logout={logout}
        isDev={isDev} goJob={goJob} notify={notify} setDevUser={setDevUser} goNewJob={goNewJob} goTerms={goTerms}
        loginWithRedirect={loginWithRedirect} getAccessTokenSilently={getAccessTokenSilently} isAuthenticated={isAuthenticated}
        onPhoneUpdated={(phone) => setDbUser(prev => ({ ...prev, phone }))}
        fontLabel={fontLabel} cycleFontSize={cycleFontSize} highContrast={highContrast} toggleContrast={toggleContrast}
        {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "driver") {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><DriverScreen
        currentUser={currentUser} isAuthenticated={isAuthenticated} loginWithRedirect={loginWithRedirect}
        getAccessTokenSilently={getAccessTokenSilently} goJob={goJob} goFeed={goFeed} notify={notify}
        {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "admin" && currentUser?.role === 'admin') {
    return (
      <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><AdminScreen
        currentUser={currentUser} goFeed={goFeed} goJob={goJob} notify={notify}
        getAccessTokenSilently={getAccessTokenSilently} isAuthenticated={isAuthenticated}
        {...navProps}
      /></Suspense></ErrorBoundary></div></>
    );
  }

  if (screen === "terms") {
    return <><OfflineIndicator /><PwaInstallBanner /><div key={screen} ref={mainRef} tabIndex={-1} className="screen-enter" style={{ outline: "none" }}><ErrorBoundary key={screen}><Suspense fallback={lazyFallback}><TermsScreen goFeed={goFeed} goBack={() => setScreen(selectedJob ? "job" : "mypage")} {...navProps} /></Suspense></ErrorBoundary></div></>;
  }

  // Fallback
  return <><OfflineIndicator /><div style={baseStyle}><div style={{ padding: 40, textAlign: "center", color: theme.textDim }}>Hleð...</div></div></>;
}
